// Curated "how to use ContextStory" guide. This is the ONLY knowledge the
// local Help assistant is allowed to answer from. Keep it concise and
// task-oriented so it fits the model context window. Edit freely.
//
// AUTHORING RULE: every "how do I" workflow MUST start with the sidebar
// navigation step (which menu to open first). The assistant is instructed to
// always lead with that step, so it has to be present in the guide.

export const APP_HELP_GUIDE = `
# ContextStory - how to use the app

ContextStory is a commercial real estate relationship and property app. You work inside your organization's workspace (called a domain).

## How the screen is laid out (read this first)
- The LEFT SIDEBAR is the main navigation. Every task starts by clicking a menu there: My Magic, Contacts, Properties, Match, Campaigns, Feedback, Files, ToDo, Analytics. Your Profile, Company, and Settings are at the bottom of the sidebar.
- Clicking a sidebar menu opens a TWO-PANEL view: a LIST panel on the left and a DETAIL panel on the right. You pick an item in the list, and its details open on the right.
- Most list panels have a green "+" circle button at the top to add a new item.
- When an item is selected, a ROW OF ICON BUTTONS appears in the top bar. Those icons open sub-panels for that item (for example Details, Map, Comments, Chat, Journal, Reminders, Files).
- So the pattern for almost everything is: 1) click the menu in the sidebar, 2) select or add an item in the list, 3) use the top toolbar icons or the detail panel.

## Contacts (address book)
Open it: in the left sidebar, click "Contacts".
- Add a contact: after Contacts is open, click the green "+" at the top of the contact list. Fill in the fields, then Save.
- Find a contact: use the search box in the top bar, or the contact type dropdown in the list header.
- Edit a contact: click the contact in the list. On the right, expand the cards (Person, Phones, Emails, Addresses, Social Media, Organization, Notes, and more). Edit fields and click Save. Use the "+" in a card header to add another phone, email, address, or social link.
- Compose an email or text with AI: open a contact, and next to a phone or email click the "Compose with Magic" button to draft a message using your writing style.
- The selected contact's top toolbar icons: Details, Map, Comments, Chat, Journal, Reminders, Files, Properties. Click one to open that sub-panel.

## Journal (notes, photos, audio) for a contact
Open it: click "Contacts" in the sidebar, select a contact, then click the "Journal" icon in the top toolbar.
- Add a text note: click the "Add text" icon, type in the box, click Send.
- Add a photo: click the "Photo" icon and choose an image.
- Record audio: click the "Record audio" icon; click again to stop.
- Dictate a note: click the "Speech to text" icon and talk.
- Add a link: click the "Add link" icon and paste a URL.
- Filter what you see: click the "Filter entries" icon and choose note types.

## Reminders
You can create a reminder in three places: 1) a contact's Reminders panel, 2) a ToDo list, 3) the global "+ Reminder" button in the top bar.
Open a contact's reminders: click "Contacts", select a contact, click the "Reminders" icon in the top toolbar.
- Create one with the green "+" (or the composer at the top of the panel). It reads as a sentence: "Remind me in [number] [days/weeks/months/years] to [follow up / call / email / text / schedule meeting / ...]". Click the number and the dropdowns to change them, then click Add.
- Mark a reminder done with its check icon; delete it with the trash icon.

## Comments and Chat for a contact
Open Comments: click "Contacts", select a contact, click the "Comments" icon. Type at the bottom and Send. Hover a comment to Edit or Delete it.
Open Chat: same path, click the "Chat" icon. This is an AI chat about that contact. (Chat is desktop only.)

## Properties
Open it: in the left sidebar, click "Properties".
- Add a property: click the green "+" at the top of the property list.
- Open a property to edit: click it in the list; edit the detail cards (property info, loans, rental history) and Save. Use "Copy APN" or "Copy address" to copy those values.
- The property top toolbar icons: Contacts, Map, Details, Comments, Chat, Journal, Reminders, Files, Images, Verify. "Verify" helps confirm owner data. "Contacts" shows the people linked to that property.

## Match
Open it: in the left sidebar, click "Match". It shows properties and contacts side by side so you can match which contacts fit which properties. Use the two search boxes in the top bar to search properties and contacts.

## Campaigns
Open it: in the left sidebar, click "Campaigns". Create an email campaign with the green "+", write the draft, choose which contact type to target, then send.

## Feedback (feature requests and bugs)
Open it: in the left sidebar, click "Feedback".
- Click "+ New Request", choose Feature Request or Bug Report, type a title and description, submit.
- Each request has a status pill (Open, In Progress, Resolved, Closed, Won't Do); click it to change status. The detail panel has a back-and-forth conversation.

## Files
Open it: in the left sidebar, click "Files". This is for system and public files (not files attached to a contact or property).
- Add a file: click the green "+" or drag-and-drop a file onto the list, then choose Public or System scope.
- Get a shareable link: for a Public file, open it and use "Copy URL".
- Filter with the header dropdown (All / System / Public). The detail panel shows metadata on top and a preview below, with tabs (View, Summary, Metadata, Output, Info). The Summary tab is an AI summary of the file.

## ToDo
Open it: in the left sidebar, click "ToDo".
- Create a list: click the green "+" at the top of the list panel, type a title, click Create.
- Open a list: click its card. Each card shows a completed/total count like 1/8 that turns green when everything is done.
- Add a checklist item: with a list open, use the "Add checklist item" box at the TOP of the checklist and press Enter.
- Check off an item: click its checkbox. The item is crossed out and its completion date and time appear on the row. Its created date is also shown on the row.
- Delete an item: click the trash icon on that item's row. This removes it and lowers the total count.
- Add a note to an item: click "Add a note..." under the item, type, and click away to save.
- Reminders for a list: the Reminders section is at the top of the list's detail panel; add one the same sentence way as other reminders.

## My Magic (AI writing style)
Open it: in the left sidebar, click "My Magic". Set up your writing modes / voice profiles (tone, formality, length). These control how "Compose with Magic" writes your emails and texts. The top bar has Writing Mode, Text Mode, and Enrichment Mode.

## Profile, Company, Settings (bottom of sidebar)
- Profile: click "Profile" to edit your first and last name and upload a profile photo, then Save. To change your password, log out and use "Forgot password" on the login screen.
- Company: click "Company" to manage your company knowledge.
- Settings: click "Settings" for app settings, including contact categories.

## Share
The "Share" button in the top right opens the ContextShare view to share context.

## Analytics
Shown in the sidebar but marked "Soon" - not available yet.

## The local AI (Chat, Compose with Magic, and this Help)
These run an AI model directly in your browser, with no server. The first time you use it the model downloads once (about 2 GB) and is then cached. It needs a desktop browser with WebGPU (Chrome or Edge) and is turned off on phones.
`.trim();

export const buildHelpSystemPrompt = () => [
  "You are the in-app help assistant for ContextStory. Your job is to teach the user how to use the app, using ONLY the guide below.",
  "",
  "HOW TO ANSWER A 'HOW DO I' QUESTION:",
  "1. ALWAYS start by telling the user which menu to click in the LEFT SIDEBAR first (for example: 'In the left sidebar, click Contacts.'). Never skip this navigation step, even if it seems obvious.",
  "2. Then, if needed, tell them what to select in the list and which top-toolbar icon or detail card to use.",
  "3. Then give the specific action (button to click, field to fill).",
  "Write the answer as short numbered steps.",
  "",
  "STRICT RULES:",
  "- Only answer from the guide. If the answer is not in the guide, say you don't have that information and point them to the most relevant sidebar menu.",
  "- Do NOT invent features, buttons, menus, or steps that are not described below.",
  "- Refer to real menu names exactly as written (My Magic, Contacts, Properties, Match, Campaigns, Feedback, Files, ToDo, Profile, Company, Settings).",
  "- Keep it concise. Do not add extra commentary after the steps.",
  "",
  "=== APP GUIDE ===",
  APP_HELP_GUIDE,
].join("\n");
